import {
  REFRACTORIES_WITH_CUSTOMIZATION_QUERY_KEY,
  type ErrorResponse
} from '@domain/refractory-product-catalog'
import {ApiClient} from '@hconnect/apiclient'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import type {AxiosError} from 'axios'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useRefraModal} from '../../providers'
import {logger} from '../../utils'

interface AddRefractoryDocumentsVariables {
  refractoryId: number
  documents: File[]
}

export const useAddRefractoryDocuments = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {closeModal} = useRefraModal()
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError<ErrorResponse>, AddRefractoryDocumentsVariables>({
    mutationFn: async ({refractoryId, documents}: AddRefractoryDocumentsVariables) => {
      const formData = new FormData()
      documents.forEach((document) => formData.append('documents', document))

      await ApiClient.axiosInstance.post<void>(
        `/plants/refractory-product-data-catalog/refractories/${refractoryId}/documents`,
        formData,
        {headers: {'content-type': 'multipart/form-data'}}
      )
    },
    onError: (error) => {
      const errorDetails = error?.response?.data?.detail ?? error.message ?? error.response?.status
      logger
        .context('useAddRefractoryDocuments')
        .extras({errorDetails})
        .error('Error occurred while adding refractory documents')

      spawnErrorToast(t('error.addRefractoryDocuments'))
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === REFRACTORIES_WITH_CUSTOMIZATION_QUERY_KEY
      })
      spawnSuccessToast(t('success.addRefractoryDocuments'))
    },
    onSettled: () => closeModal()
  })
}
